import doesFolderIdEqualName from '../utils/doesFolderIdEqualName';
import FindConversationJsonResponse from 'owa-service/lib/contract/FindConversationJsonResponse';
import FindFolderJsonResponse from 'owa-service/lib/contract/FindFolderJsonResponse';
import FindItemJsonResponse from 'owa-service/lib/contract/FindItemJsonResponse';
import FindItemResponseMessage from 'owa-service/lib/contract/FindItemResponseMessage';
import OwaUserConfiguration from 'owa-service/lib/contract/OwaUserConfiguration';
import UnifiedGroupsSetsType from 'owa-service/lib/contract/UnifiedGroupsSetsType';
import { SessionData } from 'owa-service/lib/getSessionData';
import ServerVersionInfo from 'owa-service/lib/contract/ServerVersionInfo';

let sessionDataResponses = {
    findConversationResponse: null,
    findFoldersResponse: null,
    findMailFolderItemResponse: null,
    groupsSets: null,
    exchangeServerVersionInfo: null,
};

export function getExchangeServerVersionInfo(): ServerVersionInfo {
    return sessionDataResponses.exchangeServerVersionInfo as ServerVersionInfo;
}

export function stashSessionDataResponses(
    sessionData: SessionData,
    userConfig: OwaUserConfiguration
) {
    sessionDataResponses.findConversationResponse = sessionData.findConversation;
    sessionDataResponses.findFoldersResponse = sessionData.findFolders;
    sessionDataResponses.findMailFolderItemResponse = sessionData.findMailFolderItem;
    sessionDataResponses.groupsSets = userConfig ? userConfig.GroupsSets : null;

    if (sessionData?.findFolders?.Header) {
        sessionDataResponses.exchangeServerVersionInfo =
            sessionData.findFolders.Header.ServerVersionInfo;
    }
}

export function tryConsumeFindConversationResponse(
    folderId: string,
    startIndex: number
): FindConversationJsonResponse {
    if (!sessionDataResponses.findConversationResponse) {
        return null;
    }

    const findConversationResponse: FindConversationJsonResponse =
        sessionDataResponses.findConversationResponse;

    // Always clear out the stash when consuming findConversation response from session data regardless of the folderId.
    // even if we're not requesting inbox. We want to get a fresh response when user navigates to Inbox later
    sessionDataResponses.findConversationResponse = null;

    if (
        shouldReturnSessionDataResponse(
            folderId,
            findConversationResponse.Body.Conversations,
            startIndex,
            findConversationResponse.Body.IndexedOffset
        )
    ) {
        return findConversationResponse;
    }

    return null;
}

export function tryConsumeFindMailFolderItemResponse(
    folderId: string,
    startIndex: number
): FindItemJsonResponse {
    if (!sessionDataResponses.findMailFolderItemResponse) {
        return null;
    }

    const findMailFolderItemResponse: FindItemJsonResponse =
        sessionDataResponses.findMailFolderItemResponse;

    // Always clear out the stash when consuming findItem response from session data regardless of the folderId.
    // even if we're not requesting inbox. We want to get a fresh response when user navigates to Inbox later
    sessionDataResponses.findMailFolderItemResponse = null;

    // Only return session data if the folder being requested is Inbox, and the startIndex in server response matches client settings
    const findItemRootFolder = (findMailFolderItemResponse.Body.ResponseMessages
        .Items[0] as FindItemResponseMessage).RootFolder;

    if (
        findItemRootFolder &&
        shouldReturnSessionDataResponse(
            folderId,
            findItemRootFolder.Items,
            startIndex,
            findItemRootFolder.IndexedPagingOffset
        )
    ) {
        return findMailFolderItemResponse;
    }

    return null;
}

/**
 * The session data findFolders response shall be consumed when OWA boots
 * and when primary account folders load. After that the session data findFolders
 * response gets set to null and hence there shall be no issues of this response
 * getting accidentally consumed for connected accounts which load only on demand.
 */
export function tryConsumeFindFoldersResponse(): FindFolderJsonResponse {
    if (!sessionDataResponses.findFoldersResponse) {
        return null;
    }

    let findFoldersResponse = sessionDataResponses.findFoldersResponse;

    // Clear out the response after consuming it
    sessionDataResponses.findFoldersResponse = null;
    return findFoldersResponse;
}

export function tryConsumeGroupSetsFromSessionData(): UnifiedGroupsSetsType {
    if (!sessionDataResponses.groupsSets) {
        return null;
    }

    let groupsSets = sessionDataResponses.groupsSets;

    // Clear out the response after consuming it
    sessionDataResponses.groupsSets = null;
    return groupsSets;
}

function shouldReturnSessionDataResponse(
    folderId: string,
    items: any[],
    startIndex: number,
    indexOffset: number
) {
    // Only return session data if the folder being requested is Inbox, and the startIndex in server response matches client settings
    if (doesFolderIdEqualName(folderId, 'inbox')) {
        // items could be null when there is error in findConversation/finditem call
        return items && startIndex === items.length - indexOffset;
    }

    return false;
}
